import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { env } from '@/config'
import { AuthContext, type AuthContextValue, type AuthStatus } from '@/contexts/auth-context'
import { authApi, sessionManager, type LoginCredentials, type SessionUser } from '@/services/auth'
import { clientLogger } from '@/services/telemetry'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    env.authEnabled ? 'initializing' : 'disabled',
  )
  const [user, setUser] = useState<SessionUser | undefined>(undefined)

  useEffect(() => {
    if (!env.authEnabled) return

    const unsubscribe = sessionManager.subscribe((snapshot) => {
      setUser(snapshot.user)
      setStatus(snapshot.user ? 'authenticated' : 'unauthenticated')
    })

    // A valid refresh cookie restores the session across reloads; failure just
    // means the visitor is anonymous, which is not an error worth surfacing.
    void sessionManager.refresh().then((restored) => {
      if (!restored) setStatus('unauthenticated')
    })

    return unsubscribe
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials)
    sessionManager.start(response.user, {
      accessToken: response.accessToken,
      expiresAt: Date.now() + response.expiresIn * 1000,
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch (error) {
      // The local session must be dropped even if the server call fails.
      clientLogger.warn('Logout request failed; clearing local session anyway', {
        reason: String(error),
      })
    } finally {
      sessionManager.clear()
    }
  }, [])

  const getAccessToken = useCallback(() => sessionManager.getAccessToken(), [])

  const hasRole = useCallback(
    (...roles: string[]) => {
      if (!env.authEnabled) return true
      if (roles.length === 0) return true
      return Boolean(user?.role && roles.includes(user.role))
    },
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'disabled' || status === 'authenticated',
      login,
      logout,
      getAccessToken,
      hasRole,
    }),
    [status, user, login, logout, getAccessToken, hasRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
